import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export type FormPickerCoordinatorValue = {
  /** Formik field `name` of the picker that last opened (debug / optional sync) */
  activeKey: string | null;
  /**
   * Register a callback that force-closes this field’s picker UI when another
   * field calls `notifyOpen`. Returns unregister cleanup.
   */
  registerCloser: (fieldName: string, close: () => void) => () => void;
  /** Close every other registered picker, then mark `fieldName` as active. */
  notifyOpen: (fieldName: string) => void;
  /** Clear active key when the owning field closes its own picker. */
  notifyClose: (fieldName: string) => void;
};

const FormPickerCoordinatorContext =
  createContext<FormPickerCoordinatorValue | null>(null);

const noop = () => {};

const fallbackValue: FormPickerCoordinatorValue = {
  activeKey: null,
  registerCloser: () => noop,
  notifyOpen: noop,
  notifyClose: noop,
};

/**
 * Wrap a GenerateSchemaForm (or subtree) so opening one dropdown/picker runs
 * every other field’s registered closer first.
 */
export function FormPickerCoordinatorProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const closersRef = useRef(new Map<string, () => void>());

  const registerCloser = useCallback((fieldName: string, close: () => void) => {
    closersRef.current.set(fieldName, close);
    return () => {
      closersRef.current.delete(fieldName);
    };
  }, []);

  const notifyOpen = useCallback((fieldName: string) => {
    closersRef.current.forEach((close, key) => {
      if (key !== fieldName) {
        close();
      }
    });
    setActiveKey(fieldName);
  }, []);

  const notifyClose = useCallback((fieldName: string) => {
    setActiveKey((cur) => (cur === fieldName ? null : cur));
  }, []);

  const value = useMemo(
    () => ({ activeKey, registerCloser, notifyOpen, notifyClose }),
    [activeKey, registerCloser, notifyOpen, notifyClose],
  );

  return (
    <FormPickerCoordinatorContext.Provider value={value}>
      {children}
    </FormPickerCoordinatorContext.Provider>
  );
}

export function useFormPickerCoordinator(): FormPickerCoordinatorValue {
  return useContext(FormPickerCoordinatorContext) ?? fallbackValue;
}
